import { useCallback, useEffect, useRef, useState } from 'react'

// Ein Abschnitt gilt als aktiv, sobald seine Oberkante im oberen Drittel des
// Viewports steht. Ohne den unteren Abzug waeren beim Scrollen staendig
// mehrere Abschnitte gleichzeitig sichtbar und die Markierung sprunghaft.
const ROOT_MARGIN = '-96px 0px -66% 0px'

/**
 * Markiert den Abschnitt, in dem der Nutzer gerade liest, und springt auf
 * Wunsch zu einem anderen.
 *
 * Tolerant gegenueber fehlendem IntersectionObserver (jsdom, alte Browser):
 * dann bleibt der zuletzt bewusst gewaehlte Abschnitt markiert. Die
 * Navigation funktioniert weiter, sie folgt nur nicht mehr dem Scrollen.
 */
export const useActiveSection = (ids) => {
  const [activeId, setActiveId] = useState(ids[0])

  // Nach einem Klick auf die Navigation laeuft das Smooth-Scrolling noch,
  // waehrenddessen wuerde der Observer durch die uebersprungenen Abschnitte
  // hindurchmarkieren. Bis der Zielabschnitt erreicht ist, gewinnt die Wahl.
  const pinnedRef = useRef(null)

  const key = ids.join('|')

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting)
        if (!visible.length) return

        const topmost = visible.reduce((a, b) =>
          a.boundingClientRect.top <= b.boundingClientRect.top ? a : b
        )

        if (pinnedRef.current) {
          if (pinnedRef.current !== topmost.target.id) return
          pinnedRef.current = null
        }

        setActiveId(topmost.target.id)
      },
      { rootMargin: ROOT_MARGIN, threshold: 0 }
    )

    key
      .split('|')
      .map((id) => document.getElementById(id))
      .filter(Boolean)
      .forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [key])

  const scrollToSection = useCallback((id) => {
    pinnedRef.current = id
    setActiveId(id)

    // scroll-mt an den Abschnitten haelt die Ueberschrift frei, deshalb reicht
    // hier block: 'start'.
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }, [])

  return { activeId, scrollToSection }
}
